import React from 'react';

import { Input, Select, Switch } from 'antd';
import { useIntl } from 'react-intl';

import Title from '@src/components/BoxContainer/components/Title';
import type {
  FilterValues,
  SetValueProps,
} from '@src/components/FilterDropDown/FilterDropDown.interfaces';
import { KeyValueWidget } from '@src/components/KeyValueWidget';
import IntlMessages from '@src/util/IntlMessages';

import {
  ACTIVE_USER_FILTER_FIELDS,
  buildActiveUserStalledFilterSetValueProps,
  buildActiveUserLowBatteryFilterSetValueProps,
  buildActiveUserStatusFilterSetValueProps,
  buildActiveUserTextFilterSetValueProps,
} from '../../activeUserFilterConfig';

import styles from './ActiveUserDashboardFilters.module.less';

interface ActiveUserDashboardFiltersProps {
  handleFilters: (props: SetValueProps) => void;
  filters: FilterValues;
}

const ActiveUserDashboardFilters: React.FC<ActiveUserDashboardFiltersProps> = ({
  handleFilters,
  filters,
}) => {
  const intl = useIntl();
  const statusValue = filters[ACTIVE_USER_FILTER_FIELDS.STATUS]?.value;
  const stalledValue = filters[ACTIVE_USER_FILTER_FIELDS.STALLED]?.value === true;
  const lowBatteryValue = filters[ACTIVE_USER_FILTER_FIELDS.LOW_BATTERY]?.value === true;

  const statusOptions = [
    {
      label: <IntlMessages id="dashboards.active_user.filters.show.connected" />,
      value: true as boolean,
    },
    {
      label: <IntlMessages id="dashboards.active_user.filters.show.no.connected" />,
      value: false as boolean,
    },
    {
      label: <IntlMessages id="general.all" />,
      value: null as boolean | null,
    },
  ];

  return (
    <div className={styles.root}>
      <KeyValueWidget
        labelClass="gx-guarnic-pb-1"
        label={<Title.Filter value={<IntlMessages id="dashboards.active_user.filters" />} />}
        value={
          <Input.Search
            className={`gx-mb-0 ${styles.field}`}
            placeholder={intl.formatMessage({ id: 'activeUserDashboard.text.placeholder' })}
            value={(filters[ACTIVE_USER_FILTER_FIELDS.TEXT]?.value as string) ?? ''}
            onChange={event =>
              handleFilters(buildActiveUserTextFilterSetValueProps(event.target.value, intl))
            }
            allowClear
          />
        }
      />
      <KeyValueWidget
        labelClass="gx-guarnic-pb-1"
        label={<Title.Filter value={<IntlMessages id="dashboards.active_user.filter.show" />} />}
        value={
          <Select
            className={`gx-w-100 ${styles.field}`}
            options={statusOptions}
            value={statusValue === undefined ? true : (statusValue as boolean | null)}
            onChange={value => handleFilters(buildActiveUserStatusFilterSetValueProps(value, intl))}
          />
        }
      />
      <KeyValueWidget
        labelClass="gx-guarnic-pb-1"
        label={<Title.Filter value={<IntlMessages id="activeUserDashboard.filter.stalled" />} />}
        value={
          <Switch
            checked={stalledValue}
            onChange={checked =>
              handleFilters(buildActiveUserStalledFilterSetValueProps(checked, intl))
            }
          />
        }
      />
      <KeyValueWidget
        labelClass="gx-guarnic-pb-1"
        label={
          <Title.Filter value={<IntlMessages id="activeUserDashboard.filter.lowBattery" />} />
        }
        value={
          <Switch
            checked={lowBatteryValue}
            onChange={checked =>
              handleFilters(buildActiveUserLowBatteryFilterSetValueProps(checked, intl))
            }
          />
        }
      />
    </div>
  );
};

export default React.memo(ActiveUserDashboardFilters);
